import React, {useEffect, useRef, useState} from "react";
import {Factoid} from "./Factoid";
import {Button} from "@progress/kendo-react-buttons";
import {AppBar, AppBarSection} from "@progress/kendo-react-layout";
import {AudioMotionSoundVisualizer} from "./AudioMotionSoundVisualizer";
import {SvgIcon} from "@progress/kendo-react-common";
import {playIcon} from "@progress/kendo-svg-icons";
import {Quiz} from "../models/Quiz";
import {GearsScoreboard} from "./GearsScoreboard";
import {ResultsView} from "./ResultsView";
import {recordLap} from "../services/historyService";
import {isFriday} from "../utils/date";
import {resolveSound, SoundMap} from "../services/soundCache";

interface GameScreenProps {
    quiz: Quiz;
    sounds: SoundMap;
    buttonClick: (requestedAction: () => void) => void;
    stopPlaying: () => void;
}

const FACTOID_DELAY_MS = 800;

export const GameScreen = ({quiz, sounds, buttonClick, stopPlaying}: GameScreenProps) => {
    const [index, setIndex] = useState(0);
    /** Zero-based index of the chosen option; null while the question is open. */
    const [selected, setSelected] = useState<number | null>(null);
    const [scoreBoard, setScoreBoard] = useState<Array<boolean | undefined>>(
        () => new Array(quiz.questions.length).fill(undefined));
    const [showResult, setShowResult] = useState(false);
    const [showSound, setShowSound] = useState(true);
    const [lapsToday, setLapsToday] = useState(1);
    const [daysInARow, setDaysInARow] = useState(1);
    const factoidTimer = useRef<ReturnType<typeof setTimeout>>();

    const question = quiz.questions[index];
    const hardMode = isFriday();

    useEffect(() => () => clearTimeout(factoidTimer.current), []);

    const checkAnswer = (optionIndex: number) => {
        if (selected !== null) {
            return;
        }
        setSelected(optionIndex);
        setScoreBoard((prev) => {
            const next = [...prev];
            next[index] = optionIndex === question.answer;
            return next;
        });
        factoidTimer.current = setTimeout(() => setShowSound(false), FACTOID_DELAY_MS);
    };

    const optionClassName = (optionIndex: number): string => {
        if (selected === null) {
            return "";
        }
        if (optionIndex === question.answer) {
            return "correct";
        }
        return optionIndex === selected ? "wrong" : "";
    };

    const next = () => {
        if (selected === null) {
            return;
        }
        clearTimeout(factoidTimer.current);
        if (index === quiz.questions.length - 1) {
            const history = recordLap(scoreBoard.map(Boolean));
            setLapsToday(history.lapsToday ?? 1);
            setDaysInARow(history.daysInARow ?? 1);
            setShowResult(true);
            return;
        }
        setIndex((prev) => prev + 1);
        setSelected(null);
        setShowSound(true);
    };

    const clickNext = () => {
        buttonClick(next);
    };

    const soundUrl = resolveSound(sounds,
        hardMode && question.soundUrl1s ? question.soundUrl1s : question.soundUrl);

    return (
        <div className={"container flex-wrapper"}>
            {showResult ?
                <ResultsView scoreBoard={scoreBoard} total={quiz.questions.length}
                             lapsToday={lapsToday} daysInARow={daysInARow}
                             playedToday={true} onReplay={stopPlaying}/> :
                <>
                    <ul>
                        {question.options.map((option, i) => (
                            <li key={`${index}-${i}`} className={optionClassName(i)}
                                onClick={() => checkAnswer(i)}>{option}</li>
                        ))}
                    </ul>
                    <div>
                        {showSound ?
                            <AudioMotionSoundVisualizer key={index} soundUrl={soundUrl}
                                                        maxSeconds={hardMode && !question.soundUrl1s ? 1 : undefined}/> :
                            <>
                                <Factoid text={question.factoid}/>
                                <Button disabled={selected === null} className={"next-button"}
                                        onClick={clickNext}>NEXT&nbsp;<SvgIcon size={"xlarge"} icon={playIcon}/></Button>
                            </>
                        }
                    </div>
                    <AppBar position={"bottom"} style={{backgroundColor: "inherit"}}>
                        <AppBarSection style={{width: "30%", float: "left"}}>
                            <GearsScoreboard scoreBoard={scoreBoard} className="gears"/>
                        </AppBarSection>
                        <AppBarSection style={{width: "65%", float: "right"}}>
                            <img className={"footer-logo"} src={"./images/geard_logo.svg"} alt={"gEARED!!"}/>
                        </AppBarSection>
                    </AppBar>
                </>}
        </div>
    );
};
